/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import CommentIcon from '@atlaskit/icon/core/comment';
import { RovoLogo } from '@atlaskit/logo';
import Modal, { ModalBody, ModalFooter, ModalHeader } from '@atlaskit/modal-dialog';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { ContextualAnalyticsData, FireScreenAnalytics, MODAL } from '@atlassian/analytics-bridge';

import { type RovoReintroductionModalProps } from '../../types';

import { messages } from './messages';
import { PromptBoxVideo } from './prompt-box-video';

const SOURCE_NAME = 'rovoReintroductionModal';

/**
 * Background colour of the hero container. Matches the prompt-box video clip's
 * baked-in background (#F8F8F8) so the two read as a single surface. Defined
 * locally because `@atlaskit/ui-styling-standard` forbids importing values used
 * inside `cssMap`; the prompt-box video keeps its own matching copy.
 */
const HERO_BACKGROUND_COLOR = '#F8F8F8';

const styles = cssMap({
	hero: {
		position: 'relative',
		// The prompt-box video bleeds past the right/bottom edges; clip the overflow.
		overflow: 'hidden',
		display: 'grid',
		gridTemplateColumns: '1fr 1fr',
		alignItems: 'stretch',
		columnGap: token('space.300'),
		// Pin the hero to the prompt-box video height so the container is exactly as
		// tall as the video and the two blend seamlessly (no grey peeking above/below).
		minHeight: '300px',
		borderRadius: token('radius.large', '8px'),
		// Match the video's baked-in background so the container and the clip blend.
		// `cssMap`'s `backgroundColor` is typed to design tokens only; cast to allow
		// the raw hex required to match the video's baked-in background.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		backgroundColor: HERO_BACKGROUND_COLOR as any,
	},
	// Dotted grid pattern rendered on its own layers so the horizontal fade mask
	// can taper it off (around the "s" in "myself") without affecting the solid
	// background or the hero content. The vertical and horizontal dotted lines are
	// drawn as two separate single-gradient layers (a comma-separated
	// background-size list does not apply reliably per-image in this pipeline).
	patternVertical: {
		position: 'absolute',
		insetBlockStart: 0,
		insetBlockEnd: 0,
		insetInlineStart: 0,
		insetInlineEnd: 0,
		zIndex: 0,
		pointerEvents: 'none',
		// One round dot per 24px-wide column, repeated every 4px down -> vertical
		// dotted lines. `circle` is required: the default ellipse shape stretches
		// the dot to the non-square tile and renders dashes instead of dots.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		backgroundImage: `radial-gradient(circle, ${token('color.border')} 1px, transparent 1px)`,
		backgroundSize: '24px 4px',
		// `-webkit-mask-image` is added automatically by the build's autoprefixer.
		maskImage:
			'linear-gradient(to right, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 1) 21%, rgba(0, 0, 0, 0) 38%)',
	},
	patternHorizontal: {
		position: 'absolute',
		insetBlockStart: 0,
		insetBlockEnd: 0,
		insetInlineStart: 0,
		insetInlineEnd: 0,
		zIndex: 0,
		pointerEvents: 'none',
		// One round dot per 24px-tall row, repeated every 4px across -> horizontal
		// dotted lines. `circle` is required (see patternVertical above).
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		backgroundImage: `radial-gradient(circle, ${token('color.border')} 1px, transparent 1px)`,
		backgroundSize: '4px 24px',
		// `-webkit-mask-image` is added automatically by the build's autoprefixer.
		maskImage:
			'linear-gradient(to right, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 1) 21%, rgba(0, 0, 0, 0) 38%)',
	},
	heroTextColumn: {
		position: 'relative',
		zIndex: 1,
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
		gap: token('space.400'),
		paddingTop: token('space.500'),
		paddingRight: token('space.500'),
		paddingBottom: token('space.500'),
		paddingLeft: token('space.500'),
	},
	body: {
		paddingTop: token('space.300'),
		paddingBottom: token('space.100'),
	},
});

export function RovoReintroductionModal({
	analyticsAttributes,
	onClickPrimary,
	onClose,
	testId,
}: RovoReintroductionModalProps): JSX.Element {
	const { formatMessage } = useIntl();

	const handlePrimaryClick = useCallback(() => {
		onClickPrimary();
	}, [onClickPrimary]);

	const handleClose = useCallback(() => {
		onClose();
	}, [onClose]);

	return (
		<ContextualAnalyticsData
			sourceName={SOURCE_NAME}
			sourceType={MODAL}
			attributes={analyticsAttributes}
		>
			<Modal
				width="large"
				onClose={handleClose}
				testId={testId}
				label={formatMessage(messages.title)}
			>
				<ModalHeader hasCloseButton />
				<ModalBody>
					<div css={styles.hero}>
						<div css={styles.patternVertical} aria-hidden="true" />
						<div css={styles.patternHorizontal} aria-hidden="true" />
						<div css={styles.heroTextColumn}>
							<Heading size="xlarge" as="h2">
								{formatMessage(messages.title)}
							</Heading>
							<RovoLogo />
						</div>
						<PromptBoxVideo />
					</div>
					<Box xcss={styles.body}>
						<Text size="medium">{formatMessage(messages.body)}</Text>
					</Box>
				</ModalBody>
				<ModalFooter>
					<Button appearance="primary" iconBefore={CommentIcon} onClick={handlePrimaryClick}>
						{formatMessage(messages.primaryAction)}
					</Button>
				</ModalFooter>
				<FireScreenAnalytics />
			</Modal>
		</ContextualAnalyticsData>
	);
}
